import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import { shouldSplitEdge, type DensifyConfig } from '../terrain/densify';

interface Edge {
  readonly index: number;
  readonly a: Vec2;
  readonly b: Vec2;
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
  stations?: readonly Vec2[];
}

const MAX_EDGE_PASSES = 16;

function stations(a: Vec2, b: Vec2, config: DensifyConfig, depth = 0): readonly Vec2[] {
  if (depth >= MAX_EDGE_PASSES || !shouldSplitEdge(a, b, config)) return [a, b];
  const m: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  if (
    [a, b].some(
      (p) => Math.fround(p[0]) === Math.fround(m[0]) && Math.fround(p[1]) === Math.fround(m[1])
    )
  )
    return [a, b];
  return [...stations(a, m, config, depth + 1).slice(0, -1), ...stations(m, b, config, depth + 1)];
}

function shareAt(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0],
    dy = b[1] - a[1];
  return ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy);
}

function onStoredEdge(p: Vec2, { a, b }: Edge): boolean {
  const share = shareAt(p, a, b);
  if (!(share > 0 && share < 1)) return false;
  return (
    Math.fround(a[0] + (b[0] - a[0]) * share) === Math.fround(p[0]) &&
    Math.fround(a[1] + (b[1] - a[1]) * share) === Math.fround(p[1])
  );
}

function edgeHeight(
  p: Vec2,
  edge: Edge,
  heightAt: (p: Vec2) => number,
  config: DensifyConfig,
  polygon: PolygonWithHoles
): number {
  const buildStations = () => stations(edge.a, edge.b, config);
  const nodes = (edge.stations ??=
      config.triangulations?.edgeStations?.(polygon, edge.index, buildStations) ?? buildStations()),
    along = shareAt(p, edge.a, edge.b);
  for (let i = 1; i < nodes.length; i++) {
    const a = nodes[i - 1]!,
      b = nodes[i]!;
    if (along > shareAt(b, edge.a, edge.b) && i < nodes.length - 1) continue;
    const storedA: Vec2 = [Math.fround(a[0]), Math.fround(a[1])],
      storedB: Vec2 = [Math.fround(b[0]), Math.fround(b[1])],
      storedP: Vec2 = [Math.fround(p[0]), Math.fround(p[1])];
    if (storedA[0] === storedB[0] && storedA[1] === storedB[1]) return heightAt(a);
    const share = Math.max(0, Math.min(1, shareAt(storedP, storedA, storedB)));
    return heightAt(a) + (heightAt(b) - heightAt(a)) * share;
  }
  return heightAt(p);
}

/** A thin face's interior station can round onto its outline. Give that stored station the
 * same edge-only interpolation as its neighbour instead of a new independent height peak. */
export function boundaryHeight(
  polygon: PolygonWithHoles,
  heightAt: (p: Vec2) => number,
  config: DensifyConfig
): (p: Vec2) => number {
  const rings = [polygon.outer, ...polygon.holes];
  const original = new Map<number, Map<number, Vec2>>();
  for (const ring of rings)
    for (const p of ring) {
      const x = Math.fround(p[0]),
        y = Math.fround(p[1]);
      let column = original.get(x);
      if (!column) original.set(x, (column = new Map()));
      column.set(y, p);
    }
  let edgeIndex = 0;
  const edges = rings.flatMap((ring) =>
    ring.map((a, i): Edge => {
      const b = ring[(i + 1) % ring.length]!;
      const ax = Math.fround(a[0]),
        ay = Math.fround(a[1]);
      const bx = Math.fround(b[0]),
        by = Math.fround(b[1]);
      return {
        index: edgeIndex++,
        a,
        b,
        minX: Math.min(ax, bx),
        maxX: Math.max(ax, bx),
        minY: Math.min(ay, by),
        maxY: Math.max(ay, by),
      };
    })
  );
  const calculate = (p: Vec2) => {
    const x = Math.fround(p[0]),
      y = Math.fround(p[1]);
    const node = original.get(x)?.get(y);
    const edge = node
      ? undefined
      : edges.find(
          (edge) =>
            x >= edge.minX &&
            x <= edge.maxX &&
            y >= edge.minY &&
            y <= edge.maxY &&
            onStoredEdge(p, edge)
        );
    return node
      ? heightAt(node)
      : edge
        ? edgeHeight(p, edge, heightAt, config, polygon)
        : heightAt(p);
  };
  // A restored mesh visits each topology vertex once. Keep its shared-edge interpolation,
  // but avoid creating thousands of point maps whose entries will never be read again.
  if (config.triangulations?.precomputed) return calculate;
  const heights = new Map<number, Map<number, number>>();
  return (p) => {
    let column = heights.get(p[0]);
    const known = column?.get(p[1]);
    if (known !== undefined) return known;
    const height = calculate(p);
    if (!column) heights.set(p[0], (column = new Map()));
    column.set(p[1], height);
    return height;
  };
}
